import { BaseCategorizer } from './BaseCategorizer';
import type { Transaction, CategorizationResult, CategorizationContext, Rule } from '../types';
import Fuse from 'fuse.js';

/**
 * Rule-based Categorizer
 * Uses user-defined rules from categorization-rules.json
 */
export class RuleCategorizer extends BaseCategorizer {
  private rules: Rule[];

  constructor() {
    super('RuleCategorizer', 20); // Higher priority than keyword matching
    this.rules = [];
  }

  /**
   * Load rules from context
   * @param rules - Array of rule objects
   */
  setRules(rules: Rule[]): void {
    this.rules = rules || [];
  }

  async categorize(
    transaction: Transaction,
    context: CategorizationContext = {}
  ): Promise<CategorizationResult> {
    // Use rules from context if provided, otherwise use cached rules
    const rules = context.rules || this.rules;

    if (!rules || rules.length === 0) {
      return {
        category: transaction.category || 'Uncategorized',
        confidence: 0.0,
        method: 'none'
      };
    }

    const lowerDesc = transaction.description.toLowerCase();

    // Find first matching rule - try exact matching first
    for (const rule of rules) {
      if (!rule.enabled) continue;

      const pattern = rule.pattern.toLowerCase();

      // Simple substring matching for exact matches
      if (lowerDesc.includes(pattern)) {
        return {
          category: rule.category,
          confidence: 0.95, // Very high confidence for exact matches
          method: 'rule',
          ruleId: rule.id,
          person: rule.person || undefined,
          // New fields
          displayName: rule.normalizedName || undefined,
          isRecurring: rule.markAsRecurring || undefined,
          recurringType: rule.recurringType || undefined,
          excluded: rule.excludeFromAnalytics || undefined,
          exclusionReason: rule.excludeFromAnalytics ? 'rule' : undefined,
          excludedBy: rule.excludeFromAnalytics ? rule.id : undefined,
          excludedAt: rule.excludeFromAnalytics ? new Date().toISOString() : undefined
        };
      }
    }

    // Try fuzzy matching if no exact match found
    const enabledRules = rules.filter(r => r.enabled);
    if (enabledRules.length > 0) {
      const fuse = new Fuse(enabledRules, {
        keys: ['pattern'],
        threshold: 0.15, // 85% similarity
        includeScore: true
      });

      const matches = fuse.search(transaction.description);
      if (matches.length > 0 && matches[0].score !== undefined && matches[0].score <= 0.15) {
        const rule = matches[0].item;
        return {
          category: rule.category,
          confidence: 0.85, // High confidence for fuzzy matches
          method: 'rule',
          ruleId: rule.id,
          person: rule.person || undefined,
          displayName: rule.normalizedName || undefined,
          isRecurring: rule.markAsRecurring || undefined,
          recurringType: rule.recurringType || undefined,
          excluded: rule.excludeFromAnalytics || undefined,
          exclusionReason: rule.excludeFromAnalytics ? 'rule' : undefined,
          excludedBy: rule.excludeFromAnalytics ? rule.id : undefined,
          excludedAt: rule.excludeFromAnalytics ? new Date().toISOString() : undefined
        };
      }
    }

    // No rule matched
    return {
      category: transaction.category || 'Uncategorized',
      confidence: 0.0,
      method: 'none'
    };
  }

  /**
   * Apply rules to multiple transactions
   * @param transactions - Transactions to categorize
   * @param rules - Rules to apply
   * @returns Categorized transactions
   */
  async applyToMany(transactions: Transaction[], rules: Rule[]): Promise<CategorizationResult[]> {
    this.setRules(rules);
    const results: CategorizationResult[] = [];

    for (const transaction of transactions) {
      const result = await this.categorize(transaction, { rules });
      results.push(result);
    }

    return results;
  }

  getInfo() {
    return {
      ...super.getInfo(),
      type: 'rule-based',
      description: 'Categorizes transactions based on user-defined pattern matching rules',
      rulesCount: this.rules.length
    };
  }
}

/**
 * Helper function to apply rules to a transaction (backward compatibility)
 * @param description - Transaction description
 * @param rules - Array of rules
 * @returns Categorization result or null
 */
export function applyRulesToTransaction(
  description: string,
  rules: Rule[]
): Omit<CategorizationResult, 'confidence' | 'method'> | null {
  if (!rules || rules.length === 0) return null;

  const lowerDesc = description.toLowerCase();

  // Try exact matching first
  for (const rule of rules) {
    if (rule.enabled && lowerDesc.includes(rule.pattern.toLowerCase())) {
      return {
        category: rule.category,
        person: rule.person || undefined,
        displayName: rule.normalizedName || undefined,
        isRecurring: rule.markAsRecurring || undefined,
        recurringType: rule.recurringType || undefined,
        excluded: rule.excludeFromAnalytics || undefined,
        exclusionReason: rule.excludeFromAnalytics ? 'rule' : undefined,
        excludedBy: rule.excludeFromAnalytics ? rule.id : undefined,
        excludedAt: rule.excludeFromAnalytics ? new Date().toISOString() : undefined
      };
    }
  }

  // Try fuzzy matching if no exact match
  const enabledRules = rules.filter(r => r.enabled);
  if (enabledRules.length > 0) {
    const fuse = new Fuse(enabledRules, {
      keys: ['pattern'],
      threshold: 0.15, // 85% similarity
      includeScore: true
    });

    const matches = fuse.search(description);
    if (matches.length > 0 && matches[0].score !== undefined && matches[0].score <= 0.15) {
      const rule = matches[0].item;
      return {
        category: rule.category,
        person: rule.person || undefined,
        displayName: rule.normalizedName || undefined,
        isRecurring: rule.markAsRecurring || undefined,
        recurringType: rule.recurringType || undefined,
        excluded: rule.excludeFromAnalytics || undefined,
        exclusionReason: rule.excludeFromAnalytics ? 'rule' : undefined,
        excludedBy: rule.excludeFromAnalytics ? rule.id : undefined,
        excludedAt: rule.excludeFromAnalytics ? new Date().toISOString() : undefined
      };
    }
  }

  return null;
}
